// The chat's goals as the Kontext panel reads them (Kontext goal,
// .claude/analysis/kontext-goal-2026-10-06/README.md), and the pure folds the
// "Mål" group and the material's "Når det målen?" lens project from.
//
// The read (`conversationGoals`) measures every goal over ALL the chat's
// materials; each part says which measured material (`rootId`) it counts for.
// One material's lens is the same goal scoped to that root, its statuses
// re-derived by the one home of the status rules
// (`Core.Quality.GoalCoverage.objectiveStatus` / `termStatus`). A material is
// described by what it takes up, never scored against the whole goal.
//
// D56: a goal is a projection of what it references, and may hold sub-goals
// (nested, each its own `ChatGoal`). Every summary below reads the whole tree:
// a parent's coverage includes its sub-goals' objectives and terms.
//
// Pure: total folds over values. No I/O, no casts.

import * as Core from "@bilda/core";
import type { GoalSourceLink } from "./home.js";
import type { Tone } from "./LensBar.js";

export type PartLabel =
    | { readonly type: "question"; readonly ordinal: number }
    | { readonly type: "section"; readonly title: string }
    | { readonly type: "passage"; readonly excerpt: string };

// One material part a goal link points at: open `materialId` at `elementId`.
export type GoalPart = {
    readonly rootId: string;
    readonly materialId: string;
    readonly elementId: string;
    readonly label: PartLabel;
};

// Words in a source, opened with the passage marked.
export type Passage = {
    readonly sourceId: string;
    readonly sourceTitle: string;
    readonly quote: string;
    readonly page: number | undefined;
};

export type ObjectiveStatus = "met" | "partly" | "not_yet";
export type TermStatus = "defined" | "used" | "missing";

export type GoalObjective = {
    readonly id: string;
    readonly statement: string;
    readonly from: Passage | undefined;
    readonly explains: readonly GoalPart[];
    readonly practises: readonly GoalPart[];
    readonly status: ObjectiveStatus;
    // The assistant's, not kept yet: the "Suggested" mark.
    readonly suggestion: boolean;
};

// Another concept, named by its term (D55 S10).
export type ConceptName = { readonly conceptId: string; readonly text: string };

// An objective a concept serves.
export type ConceptObjective = {
    readonly id: string;
    readonly statement: string;
};

// A goal's concept, named by its term. `definition` is the agreed one (the
// concepts stage, D55 S10); `definedIn` where a source defines it.
export type GoalTerm = {
    readonly id: string;
    readonly conceptId: string;
    readonly text: string;
    readonly definition: string | undefined;
    // The live claims the definition rests on; a definition with none is
    // unsupported (knowledge-bank.md §11.5).
    readonly definitionClaims: readonly string[];
    readonly definedIn: Passage | undefined;
    readonly defines: readonly GoalPart[];
    readonly uses: readonly GoalPart[];
    readonly status: TermStatus;
    readonly broader: ConceptName | undefined;
    readonly narrower: readonly ConceptName[];
    readonly related: readonly ConceptName[];
    readonly objectives: readonly ConceptObjective[];
};

// A concept chip's link rows, in reading order, each only when it has items:
// the broader concept, the narrower ones, the related ones, the objectives it
// serves (D55 S10).
export type ConceptLinkKind = "broader" | "narrower" | "related" | "serves";
export type ConceptLinkRow = {
    readonly kind: ConceptLinkKind;
    readonly items: readonly string[];
};

export const conceptLinkRows = (term: GoalTerm): readonly ConceptLinkRow[] => {
    const rows: readonly ConceptLinkRow[] = [
        {
            kind: "broader",
            items: term.broader === undefined ? [] : [term.broader.text],
        },
        { kind: "narrower", items: term.narrower.map((c) => c.text) },
        { kind: "related", items: term.related.map((c) => c.text) },
        { kind: "serves", items: term.objectives.map((o) => o.statement) },
    ];
    return rows.filter((r) => r.items.length > 0);
};

export type Requirement = { readonly sourceId: string; readonly title: string };

// A source the goal draws on (it follows the goal into every chat).
export type GoalSource = { readonly sourceId: string; readonly title: string };

// The goal an inherited value reads through from.
export type GoalRef = { readonly contextId: string; readonly title: string };

export type ChatGoal = {
    readonly contextId: string;
    readonly title: string;
    readonly purpose: string | undefined;
    readonly audience: readonly string[];
    // Set when the audience is a parent's (the sub-goal sets none itself).
    readonly audienceFrom: GoalRef | undefined;
    readonly requirements: readonly Requirement[];
    readonly sources: readonly GoalSource[];
    readonly objectives: readonly GoalObjective[];
    readonly terms: readonly GoalTerm[];
    readonly subGoals: readonly ChatGoal[];
};

// The chat's goals, oldest first; empty = the chat has no goal yet.
export type ChatGoals = readonly ChatGoal[];

// A goal the chat can continue toward ("Fortsätt mot ett tidigare mål"):
// only goals with objectives, each with the OTHER chats that use it.
export type EarlierGoal = {
    readonly contextId: string;
    readonly title: string;
    readonly purpose: string | undefined;
    readonly objectiveCount: number;
    readonly chats: readonly EarlierChat[];
};

// A chat that worked toward an earlier goal (the host names it as the rail
// does).
type EarlierChat = { readonly id: string; readonly title: string };

// The one read: the chat's goals and what it could continue toward.
export type ChatGoalsRead = {
    readonly goals: ChatGoals;
    readonly earlier: readonly EarlierGoal[];
};

export const emptyGoalsRead: ChatGoalsRead = { goals: [], earlier: [] };

// Every goal in the tree, depth first.
const everyGoal = (goals: ChatGoals): ChatGoals =>
    goals.flatMap((g) => [g, ...everyGoal(g.subGoals)]);

// === Scoping to one material ===

const inRoot =
    (rootId: string) =>
    (parts: readonly GoalPart[]): readonly GoalPart[] =>
        parts.filter((p) => p.rootId === rootId);

// The goal as one material meets it.
export const scopedTo = (goal: ChatGoal, rootId: string): ChatGoal => {
    const own = inRoot(rootId);
    return {
        ...goal,
        objectives: goal.objectives.map((o) => {
            const explains = own(o.explains);
            const practises = own(o.practises);
            return {
                ...o,
                explains,
                practises,
                status: Core.Quality.GoalCoverage.objectiveStatus(
                    explains,
                    practises,
                ),
            };
        }),
        terms: goal.terms.map((t) => {
            const defines = own(t.defines);
            const uses = own(t.uses);
            return {
                ...t,
                defines,
                uses,
                status: Core.Quality.GoalCoverage.termStatus(defines, uses),
            };
        }),
        subGoals: goal.subGoals.map((g) => scopedTo(g, rootId)),
    };
};

// The OTHER measured materials that take an objective up (their ids), so
// "not here" never reads as "not anywhere".
export const elsewhereOf = (
    goal: ChatGoal,
    rootId: string,
    objectiveId: string,
): readonly string[] =>
    everyGoal([goal])
        .flatMap((g) => g.objectives)
        .filter((o) => o.id === objectiveId)
        .flatMap((o) => [...o.explains, ...o.practises])
        .map((p) => p.rootId)
        .filter((id, i, ids) => id !== rootId && ids.indexOf(id) === i);

// === Summaries ===

export type GoalSummary = {
    readonly objectivesMet: number;
    readonly objectivesPartly: number;
    readonly objectivesTotal: number;
    readonly termsDefined: number;
    readonly termsUsed: number;
    readonly termsTotal: number;
};

export const summaryOf = (goals: ChatGoals): GoalSummary => {
    const all = everyGoal(goals);
    const objectives = all.flatMap((g) => g.objectives);
    const terms = all.flatMap((g) => g.terms);
    return {
        objectivesMet: objectives.filter((o) => o.status === "met").length,
        objectivesPartly: objectives.filter((o) => o.status === "partly")
            .length,
        objectivesTotal: objectives.length,
        termsDefined: terms.filter((t) => t.status === "defined").length,
        termsUsed: terms.filter((t) => t.status === "used").length,
        termsTotal: terms.length,
    };
};

// One material's lens tone: attention only for what it takes up but leaves
// half done; objectives it leaves to other materials are not its fault.
export const toneOfMaterial = (s: GoalSummary): Tone =>
    s.objectivesMet + s.objectivesPartly + s.termsDefined + s.termsUsed === 0
        ? "pending"
        : s.objectivesPartly > 0 || s.termsUsed > 0
          ? "attention"
          : "clean";

// The sources the goals answer to: they live in the "Mål" group, not under
// the chat's sources ("not used yet" forever otherwise).
export const requirementIdsOf = (goals: ChatGoals): ReadonlySet<string> =>
    new Set(
        everyGoal(goals).flatMap((g) => g.requirements.map((r) => r.sourceId)),
    );

// The sources the goals draw on, each once (the first goal that has it names
// it): the Källor group lists them, marked "via målet …".
export const goalSourcesOf = (goals: ChatGoals): readonly GoalSourceLink[] =>
    everyGoal(goals)
        .flatMap((g) => g.sources.map((s) => ({ ...s, goalTitle: g.title })))
        .filter(
            (s, i, all) =>
                all.findIndex((o) => o.sourceId === s.sourceId) === i,
        );

// What to ask the agent about a gap in one objective (the author sends it).
export type Ask = "add_exercises" | "explain" | "cover";

export const askOf = (o: GoalObjective): Ask | undefined =>
    o.status === "met"
        ? undefined
        : o.explains.length > 0
          ? "add_exercises"
          : o.practises.length > 0
            ? "explain"
            : "cover";
